import { TEMPO_DIVISION_INDEX } from "@defibrillator/preset-core";
const TEMPO_BPM = 120;
function divisionBeats(division) {
    switch (division) {
        case "freeze":
            return Number.POSITIVE_INFINITY;
        case "32/1":
            return 32;
        case "16/1":
            return 16;
        case "8/1":
            return 8;
        case "4/1":
            return 4;
        case "2/1":
            return 2;
        case "1/1":
            return 1;
        case "1/2":
            return 0.5;
        case "1/4":
            return 0.25;
        case "1/8":
            return 0.125;
        case "1/16":
            return 0.0625;
        case "1/32":
            return 0.03125;
        case "1/64":
            return 0.015625;
    }
}
export function modulatorPeriodSeconds(mod) {
    if (mod.timing.mode === "tempo") {
        const beats = divisionBeats(mod.timing.division);
        if (!Number.isFinite(beats))
            return 1e9;
        return (60 / TEMPO_BPM) * beats;
    }
    return 1 / Math.max(0.01, mod.timing.frequencyHz);
}
function evalPoints(points, phase, smooth) {
    const p = ((phase % 1) + 1) % 1;
    if (points.length === 0)
        return 0;
    if (points.length === 1)
        return points[0].y;
    for (let i = 0; i < points.length - 1; i++) {
        const a = points[i];
        const b = points[i + 1];
        if (p >= a.x && p <= b.x) {
            const span = Math.max(1e-9, b.x - a.x);
            const t = (p - a.x) / span;
            if (!smooth || a.x === b.x)
                return b.y;
            return a.y + (b.y - a.y) * t;
        }
    }
    return points[points.length - 1].y;
}
function presetShape(phase, shape) {
    const t = ((phase % 1) + 1) % 1;
    switch (shape) {
        case "triangle":
            return t < 0.5 ? t * 2 : 2 - t * 2;
        case "square":
            return t < 0.5 ? 1 : 0;
        case "saw":
            return t;
        case "random":
        case "sample_and_hold":
            return ((Math.sin(t * 12.9898) * 43758.5453) % 1 + 1) % 1;
        case "sine":
        default:
            return 0.5 + 0.5 * Math.sin(2 * Math.PI * t);
    }
}
export function evaluateModulator(mod, timeSeconds, noteStart) {
    const period = modulatorPeriodSeconds(mod);
    let local = timeSeconds;
    if (mod.trigger === "note_retrigger" || mod.trigger === "one_shot") {
        local = Math.max(0, timeSeconds - noteStart);
    }
    if (mod.delaySeconds > 0 && local < mod.delaySeconds)
        return 0.5;
    const active = Math.max(0, local - mod.delaySeconds);
    let phase = (active / period + mod.phase) % 1;
    if (mod.trigger === "one_shot") {
        phase = Math.min(0.999, active / period + mod.phase);
    }
    if (mod.shape.kind === "preset") {
        return presetShape(phase, mod.shape.shape);
    }
    if (mod.shape.kind === "steps") {
        const n = mod.shape.values.length;
        const idx = Math.min(n - 1, Math.floor(phase * n));
        return mod.shape.values[idx] ?? 0;
    }
    return evalPoints(mod.shape.points, phase, mod.shape.smooth);
}
export function applyModulationMatrix(model, timeSeconds, noteStart) {
    const base = {
        voiceTranspose: model.params.voice_transpose,
        osc1Level: model.params.osc1_level,
        osc2Level: model.params.osc2_level,
        osc3Level: model.params.osc3_level,
        osc1Transpose: model.params.osc1_transpose,
        osc2Transpose: model.params.osc2_transpose,
        osc3Transpose: model.params.osc3_transpose,
        cutoff: model.params.filter_cutoff,
        resonance: model.params.filter_resonance,
        volume: model.params.volume,
        waveFrame1: model.params.osc1_wave_frame,
    };
    const sourceValue = (source) => {
        if (source.startsWith("lfo_")) {
            const index = Number(source.slice(4));
            const mod = model.modulators.find((m) => m.index === index);
            if (!mod)
                return 0.5;
            return evaluateModulator(mod, timeSeconds, noteStart);
        }
        if (source === "env_1" || source === "env_2" || source === "env_3" || source === "env_4") {
            // Approximate with sustain-biased envelope value while held.
            return 0.7;
        }
        if (source.startsWith("macro_")) {
            const index = Number(source.slice(6));
            return model.params[`macro_control_${index}`] ?? 0;
        }
        if (source === "velocity")
            return 0.8;
        if (source === "note")
            return 0.5;
        return 0.5;
    };
    for (const route of model.modulations) {
        let src = sourceValue(route.source);
        if (route.bipolar)
            src = (src - 0.5) * 2;
        const amt = route.amount * src;
        switch (route.destination) {
            case "voice_transpose":
                base.voiceTranspose += amt * 24;
                break;
            case "osc_1_level":
                base.osc1Level = Math.min(1, Math.max(0, base.osc1Level + amt));
                break;
            case "osc_2_level":
                base.osc2Level = Math.min(1, Math.max(0, base.osc2Level + amt));
                break;
            case "osc_3_level":
                base.osc3Level = Math.min(1, Math.max(0, base.osc3Level + amt));
                break;
            case "osc_1_transpose":
                base.osc1Transpose += amt * 24;
                break;
            case "osc_2_transpose":
                base.osc2Transpose += amt * 24;
                break;
            case "osc_3_transpose":
                base.osc3Transpose += amt * 24;
                break;
            case "filter_cutoff":
                base.cutoff *= 2 ** (amt * 3);
                break;
            case "filter_resonance":
                base.resonance = Math.min(1, Math.max(0, base.resonance + amt));
                break;
            case "volume":
                base.volume = Math.min(1, Math.max(0, base.volume * (route.bipolar ? 1 + amt : amt)));
                if (!route.bipolar) {
                    base.volume = Math.min(1, Math.max(0, model.params.volume * Math.max(0, src * route.amount * 1.2)));
                }
                break;
            case "osc_1_wave_frame":
                base.waveFrame1 = Math.min(256, Math.max(0, base.waveFrame1 + amt * 64));
                break;
            default:
                break;
        }
    }
    void TEMPO_DIVISION_INDEX;
    return base;
}
//# sourceMappingURL=modulation.js.map